import type { Template } from '@/api/templates';
import type { ServiceCluster, ServiceClusterTemplate } from '@/api/service-clusters';

export type TemplateUsageFilter = '' | 'manual' | 'event_driven' | 'scheduled' | 'cluster' | 'post_procedure' | 'reply_chain';

export type TemplateClusterUsage = {
  clusters: ServiceCluster[];
  links: ServiceClusterTemplate[];
};

export type TemplateUsageMeta = {
  isManual: boolean;
  isEventDriven: boolean;
  isScheduled: boolean;
  isCluster: boolean;
  isPostProcedure: boolean;
  isReplyChain: boolean;
  linkedClusters: ServiceCluster[];
};

export function getTemplateUsageMeta(
  template: Template,
  clusterUsageByTemplate: Record<string, TemplateClusterUsage>,
  eventBoundTemplateIds: Set<string>,
  autoReplyTemplateIds: Set<string>,
): TemplateUsageMeta {
  const scheduleType = template.send_schedule?.type || '';
  const clusterUsage = clusterUsageByTemplate[template.id];
  const linkedClusters = clusterUsage?.clusters || [];
  const hasAutomaticClusterTrigger = (clusterUsage?.links || []).some((link) => link.trigger_type !== 'manual');
  const hasPostProcedureClusterTrigger = (clusterUsage?.links || []).some(
    (link) => link.trigger_type === 'after_completed' || link.trigger_type === 'after_cancelled'
  );

  return {
    isManual: template.category === 'manual' || scheduleType === 'manual',
    isEventDriven: eventBoundTemplateIds.has(template.id) || scheduleType === 'event_triggered' || hasAutomaticClusterTrigger,
    isScheduled: scheduleType === 'daily_scheduled',
    isCluster: linkedClusters.length > 0,
    isPostProcedure: hasPostProcedureClusterTrigger,
    isReplyChain: autoReplyTemplateIds.has(template.id),
    linkedClusters,
  };
}

export function countTemplateUsage(
  templates: Template[],
  clusterUsageByTemplate: Record<string, TemplateClusterUsage>,
  eventBoundTemplateIds: Set<string>,
  autoReplyTemplateIds: Set<string>,
) {
  return templates.reduce(
    (acc, template) => {
      const usage = getTemplateUsageMeta(template, clusterUsageByTemplate, eventBoundTemplateIds, autoReplyTemplateIds);
      if (usage.isManual) acc.manual += 1;
      if (usage.isEventDriven) acc.eventDriven += 1;
      if (usage.isScheduled) acc.scheduled += 1;
      if (usage.isCluster) acc.cluster += 1;
      if (usage.isPostProcedure) acc.postProcedure += 1;
      if (usage.isReplyChain) acc.replyChain += 1;
      return acc;
    },
    { manual: 0, eventDriven: 0, scheduled: 0, cluster: 0, postProcedure: 0, replyChain: 0 },
  );
}

export function matchesTemplateUsageFilter(usage: TemplateUsageMeta, usageFilter: TemplateUsageFilter): boolean {
  return (
    usageFilter === '' ||
    (usageFilter === 'manual' && usage.isManual) ||
    (usageFilter === 'event_driven' && usage.isEventDriven) ||
    (usageFilter === 'scheduled' && usage.isScheduled) ||
    (usageFilter === 'cluster' && usage.isCluster) ||
    (usageFilter === 'post_procedure' && usage.isPostProcedure) ||
    (usageFilter === 'reply_chain' && usage.isReplyChain)
  );
}
